// Шаблоны поездок: /templates и /templates/<id>.
//
// Тот же каталог, что предлагается в приложении (src/templates/catalog.ts), но
// снаружи: сюда приходят из поиска по запросу «сколько стоит неделя у моря» —
// когда ещё нечего планировать и не с чем сравнивать.
//
// Ids и порядок берутся из каталога, а не из текста: список шаблонов —
// поведение приложения, и расходиться странице с ним нельзя. Тексты берутся из
// content/pages.json, потому что их же отдаёт роботу сервер.
import type { ReactElement } from 'react';
import { useI18n } from '../i18n';
import { TRIP_TEMPLATES } from '../templates';
import { pageContent } from '../content';
import { DocLayout, DOC_PATHS } from './DocLayout';
import styles from './DocLayout.module.css';

/**
 * Адрес страницы шаблона → его id. Неизвестный шаблон — не страница.
 *
 * Регистр строгий, как и на сервере (server/content.mjs): id шаблонов заданы в
 * нижнем регистре, и адрес обязан совпадать с тем, что лежит в карте сайта.
 */
export function templateIdFromPath(path: string): string | null {
  const id = /^\/templates\/([a-z][a-z0-9-]*)$/.exec(path)?.[1];
  return id && TRIP_TEMPLATES.some((tpl) => tpl.id === id) ? id : null;
}

export function TemplatesPage(): ReactElement {
  const { lang } = useI18n();
  const c = pageContent(lang).templates;

  return (
    <DocLayout title={c.title} description={c.description} lead={c.lead}>
      <h2 className={styles.h2}>{c.listTitle}</h2>
      <div className={styles.cards}>
        {TRIP_TEMPLATES.map((tpl) => {
          const item = c.items[tpl.id];
          return (
            <a key={tpl.id} className={styles.card} href={`${DOC_PATHS.templates}/${tpl.id}`}>
              <span className={styles.cardName}>{item.title}</span>
              <span className={styles.cardDesc}>{item.lead}</span>
            </a>
          );
        })}
      </div>
    </DocLayout>
  );
}

export function TemplatePage({ id }: { id: string }): ReactElement {
  const { lang } = useI18n();
  const c = pageContent(lang).templates;
  const item = c.items[id];

  return (
    <DocLayout title={item.title} description={item.description} lead={item.lead}>
      <h2 className={styles.h2}>{c.includesTitle}</h2>
      <ul className={styles.list}>
        {item.includes.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <h2 className={styles.h2}>{c.addTitle}</h2>
      <p className={styles.text}>{item.add}</p>

      {/* Обычная ссылка, а не переход внутри приложения: планировщик соберёт
          шаблон сам, увидев ?template= в адресе. Так эта же кнопка работает у
          робота и у того, кто открыл страницу без скриптов. */}
      <div className={styles.cta}>
        <a className="btn btn-primary" href={`/?template=${id}`}>
          {c.cta}
        </a>
        <span className={styles.muted}>{c.ctaNote}</span>
      </div>

      <p className={styles.muted}>
        <a className={styles.link} href={DOC_PATHS.templates}>
          {c.backToList}
        </a>
      </p>
    </DocLayout>
  );
}
